import { useEffect, useState } from "react";

const STATUS_COLOR = {
  on_point: "#34C759",
  available: "#34C759",
  with_customer: "#FF3B30",
  frozen: "#FFCC00",
  on_break: "#FFCC00",
  off_shift: "#3A3A3C",
};

const PointTimer = ({ start, totalMinutes }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!start) return null;
  const elapsed = (Date.now() - new Date(start).getTime()) / 1000;
  const total = totalMinutes * 60;
  const remaining = Math.max(0, total - elapsed);
  const m = Math.floor(remaining / 60);
  const s = Math.floor(remaining % 60);
  const pct = Math.min(100, (elapsed / total) * 100);
  const danger = remaining < 60;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">
        <span>Power Rotation</span>
        <span className={`tabular font-bold ${danger ? "text-[#FF3B30]" : "text-white"}`}>
          {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </span>
      </div>
      <div className="h-1 bg-white/10 overflow-hidden">
        <div
          className={`h-full ${danger ? "bg-[#FF3B30]" : "bg-[#34C759]"}`}
          style={{ width: `${pct}%`, transition: "width 1s linear" }}
        />
      </div>
    </div>
  );
};

const PersonRow = ({ entry, isOnPoint, rotationMinutes, large }) => {
  const color = STATUS_COLOR[entry.status] || "#3A3A3C";
  const initials = (entry.display_name || entry.name || "?").slice(0, 2).toUpperCase();

  return (
    <div
      className={`relative bg-[#141414] border ${
        isOnPoint ? "border-[#34C759] onpoint-glow" : "border-white/10"
      } rounded-sm flex items-center gap-3 px-3 py-2.5`}
      data-testid={`queue-row-${entry.user_id}`}
    >
      <div
        className={`font-display font-black tabular ${large ? "text-5xl w-14" : "text-3xl w-10"} ${
          isOnPoint ? "text-[#34C759]" : "text-white/50"
        } text-center leading-none`}
      >
        {entry.position}
      </div>
      <div className="w-9 h-9 bg-[#1F1F1F] flex items-center justify-center font-bold text-xs">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-display font-bold uppercase tracking-wide ${large ? "text-xl" : "text-base"} truncate`}>
          {entry.display_name || entry.name}
          {entry.float_active && (
            <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-[#007AFF]/20 text-[#007AFF] uppercase tracking-widest">Float</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
          <span style={{ color }} className="font-bold">{entry.status.replace("_", " ")}</span>
          {entry.frozen_reason && <span>· {entry.frozen_reason}</span>}
        </div>
      </div>
      <div className="w-1.5 h-9" style={{ backgroundColor: color }} />
    </div>
  );
};

export default function PositionList({ data, queueType, rotationMinutes = 30, title }) {
  const list = data || [];
  const onPoint = list.find((e) => e.status === "on_point");
  const others = list.filter((e) => e !== onPoint);

  return (
    <section className="space-y-3" data-testid={`position-list-${queueType}`}>
      <div className="flex items-baseline justify-between border-b border-white/10 pb-2">
        <h2 className="font-display text-2xl font-black uppercase tracking-wider">{title}</h2>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">{list.length} on roster</span>
      </div>

      {onPoint && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#34C759] font-bold mb-1.5">On Point — Next Up</div>
          <PersonRow entry={onPoint} isOnPoint rotationMinutes={rotationMinutes} large />
          <PointTimer start={onPoint.point_started_at} totalMinutes={rotationMinutes} />
        </div>
      )}

      <div className="space-y-1.5">
        {others.map((e) => (
          <PersonRow key={`${queueType}-${e.user_id}`} entry={e} rotationMinutes={rotationMinutes} />
        ))}
        {!list.length && (
          <div className="text-sm text-white/30 italic py-4 text-center">No one on roster</div>
        )}
      </div>
    </section>
  );
}
